'use client'

import { useLocale, useTranslations } from 'next-intl'

import {
  type FavoriteChangeBlock,
  formatAvailableFrom
} from '@/features/clubs/lib/favoriteChange'
import Dialog, { DialogDescription } from '@/ui/Dialog'

import S from './FavoriteDialogs.module.scss'

// BAT-1107 dialogs, laid out as Figma «Switch club» / «Rejected switching»:
// a centred display title, a short text, full-width buttons.

const TargetIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" aria-hidden>
    <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="12" cy="12" r="1.2" fill="currentColor" />
  </svg>
)

const ClockIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" aria-hidden>
    <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.6" />
    <path
      d="M12 7.5V12l3 2"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
)

const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" aria-hidden>
    <rect
      x="4"
      y="5.5"
      width="16"
      height="14.5"
      rx="2"
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <path
      d="M4 10h16M8.5 3.5v4M15.5 3.5v4"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
)

interface FavoriteSwitchDialogProps {
  open: boolean
  currentName: string
  newName: string
  pending: boolean
  onConfirm: () => void
  onCancel: () => void
}

/** US-ATH-15.1: «Your favourite is X. Do you want to support Y instead?» */
export const FavoriteSwitchDialog = ({
  open,
  currentName,
  newName,
  pending,
  onConfirm,
  onCancel
}: FavoriteSwitchDialogProps) => {
  const t = useTranslations('club.becomeFan')
  return (
    <Dialog
      open={open}
      toggle={value => {
        if (!value && !pending) {
          onCancel()
        }
      }}
      className={S.dialog}
      withoutClose
      describedByChildren
    >
      <div className={S.body}>
        <h2 className={S.title}>{t('switchTitle', { name: newName })}</h2>
        <DialogDescription className={S.text}>
          {t('switchText', { current: currentName, name: newName })}
        </DialogDescription>
        <div className={S.actions}>
          <button
            type="button"
            className={S.primary}
            onClick={onConfirm}
            disabled={pending}
          >
            {t('confirmButton')}
          </button>
          <button
            type="button"
            className={S.secondary}
            onClick={onCancel}
            disabled={pending}
          >
            {t('cancelButton')}
          </button>
        </div>
      </div>
    </Dialog>
  )
}

interface FavoriteBlockedDialogProps {
  open: boolean
  block: FavoriteChangeBlock | null
  onClose: () => void
}

/** US-ATH-15.2: why the switch is unavailable and from when it opens. */
export const FavoriteBlockedDialog = ({
  open,
  block,
  onClose
}: FavoriteBlockedDialogProps) => {
  const t = useTranslations('club.becomeFan.blocked')
  const locale = useLocale()
  const date = formatAvailableFrom(block?.availableFrom, locale)
  const reasons = block?.reasons ?? []

  return (
    <Dialog
      open={open}
      toggle={value => {
        if (!value) {
          onClose()
        }
      }}
      className={S.dialog}
      withoutClose
      describedByChildren
    >
      <div className={S.body}>
        <h2 className={S.title}>{t('title')}</h2>
        <DialogDescription className={S.text}>
          {reasons.length > 0 ? t('reasonsIntro') : t('unknown')}
        </DialogDescription>
        {reasons.length > 0 ? (
          <ul className={S.reasons}>
            {reasons.map(reason => (
              <li key={reason} className={S.reason}>
                <span className={S.reasonIcon}>
                  {reason === 'active_tasks' ? <TargetIcon /> : <ClockIcon />}
                </span>
                {reason === 'active_tasks'
                  ? t('activeTasks')
                  : reason === 'referral'
                  ? t('referral')
                  : t('cooldown')}
              </li>
            ))}
          </ul>
        ) : null}
        {reasons.includes('active_tasks') && !date ? (
          <p className={S.note}>
            <CalendarIcon />
            <span>{t('activeTasksNote')}</span>
          </p>
        ) : null}
        {date ? (
          <p className={S.note}>
            <CalendarIcon />
            <span>
              {t.rich('availableFrom', {
                date,
                accent: chunks => <span className={S.date}>{chunks}</span>
              })}
            </span>
          </p>
        ) : null}
        <div className={S.actions}>
          <button type="button" className={S.primary} onClick={onClose}>
            {t('gotIt')}
          </button>
        </div>
      </div>
    </Dialog>
  )
}
